import { createAsyncThunk, createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import authAxios from 'utils/authAxios';

// Thunks
export const fetchAdminBrokerAccounts = createAsyncThunk('adminBrokerAccount/fetchAccounts', async (filters) => {
  const params = new URLSearchParams();
  
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
  }
  
  const query = params.toString() ? `?${params.toString()}` : '';
  return authAxios.get(`/admin-api/broker-accounts/${query}`).then(response => response.data);
});

export const updateBrokerAccount = createAsyncThunk('adminBrokerAccount/update', ({ accountId, accountData }) => {
  return authAxios.put(`/admin-api/broker-accounts/${accountId}/`, accountData).then(response => response.data);
});

export const refreshBrokerAccountLedgerData = createAsyncThunk('adminBrokerAccount/refreshLedgerData', async (accountId) => {
  const response = await authAxios.post(`/admin-api/broker-accounts/${accountId}/refresh_ledger_data/`);
  
  if (response.data.success) {
    // Fetch the updated account to get the new ledger data
    const updatedAccount = await authAxios.get(`/admin-api/broker-accounts/${accountId}/`);
    return updatedAccount.data;
  }
  
  throw new Error(response.data.message || 'Failed to refresh account data');
});

// Adapter
export const adminBrokerAccountsAdapter = createEntityAdapter();

// Slice
export const adminBrokerAccountSlice = createSlice({
  name: 'adminBrokerAccount',
  initialState: adminBrokerAccountsAdapter.getInitialState({
    loading: false,
    error: null,
    refreshing: false,
  }),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminBrokerAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminBrokerAccounts.fulfilled, (state, action) => {
        adminBrokerAccountsAdapter.setAll(state, action.payload);
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchAdminBrokerAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateBrokerAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBrokerAccount.fulfilled, (state, action) => {
        adminBrokerAccountsAdapter.upsertOne(state, action.payload);
        state.loading = false;
        state.error = null;
      })
      .addCase(updateBrokerAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(refreshBrokerAccountLedgerData.pending, (state) => {
        state.refreshing = true;
        state.error = null;
      })
      .addCase(refreshBrokerAccountLedgerData.fulfilled, (state, action) => {
        adminBrokerAccountsAdapter.upsertOne(state, action.payload);
        state.refreshing = false;
        state.error = null;
      })
      .addCase(refreshBrokerAccountLedgerData.rejected, (state, action) => {
        state.refreshing = false;
        state.error = action.error.message;
      });
  }
});

// Selectors
export const {
  selectAll: selectAllAdminBrokerAccounts,
  selectById: selectAdminBrokerAccountById,
  selectIds: selectAdminBrokerAccountIds
} = adminBrokerAccountsAdapter.getSelectors(state => state.adminBrokerAccount);

export default adminBrokerAccountSlice.reducer; 